/**
 * RecordingCameraCard — one camera in the recordings picker, as an archive summary.
 *
 * The picker is a "which camera do I open" decision, and the card used to answer only "what is
 * this camera called". Everything that actually informs the choice — is there footage here, how
 * much, how far back, what is being written, and is this camera even on a mode that records
 * continuously — was one click deeper, per camera, which over a tunnel is a click and a wait each.
 *
 * The layout follows the order the decision is made, and gives each answer a different VOICE so
 * the card can be skimmed rather than read:
 *
 *   identity   name, and whether the camera is reachable, on the header row — with a chevron,
 *              because the upper part of the card is what opens the camera.
 *   the figure the stored size, alone and large. It is the number an operator compares between
 *              cards, so it gets to be the thing the eye lands on.
 *   the shape  clip count, the period covered, and how recently it last wrote — small, under it.
 *   the stream resolution / codec / bitrate / last chunk as chips, one step quieter again —
 *              detail you read when a camera looks wrong, not while scanning.
 *   recording  the footer: what this camera is set to keep, pinned to the bottom so it lines up
 *              across a row of cards. For operators with recordings.config (the caller passes
 *              `onRecordingChange`) it is the on/off switch and the type picker; for everyone
 *              else the same row, read-only. The mode is said once, here — it used to be a
 *              header badge as well, which with the picker below it said everything twice.
 *
 * The footer sits OUTSIDE the card's open button: a control nested in a button is invalid HTML,
 * and every click on it would also open the camera. The hover treatment follows that split —
 * only the open area highlights the card, so reaching for the switch doesn't read as "open".
 *
 * Shared by both products because the two pickers are the same surface over different transports;
 * everything app-specific (fetching, permissions, navigation) stays with the caller.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  VideoCameraIcon, CalendarDaysIcon, FilmIcon, ClockIcon, ChevronRightIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import StatusTag, { toneVar, type StatusTone } from '../common/StatusTag';
import Tooltip from '../common/Tooltip';
import { MODE_LABELS, recordingState } from './recordingVocab';

export interface RecordingCameraStorage {
  /** Bytes stored across every segment of this camera. */
  total_size: number;
  segment_count: number;
  oldest_segment?: string | null;
  newest_segment?: string | null;
  /**
   * The shape of what is being written NOW, taken from the newest segment that reports it —
   * not an average. A camera whose stream profile was quietly changed keeps its old total size
   * but starts writing a different picture, and that is exactly what these expose.
   */
  resolution?: string | null;
  codec?: string | null;
  bitrate_kbps?: number | null;
  /** File size of the most recent finished segment — the "is it still writing properly" figure. */
  latest_segment_size?: number | null;
}

type RecordingConfig = { enabled?: boolean; mode?: string };

export interface RecordingCameraCardProps {
  name: string;
  /**
   * LEGACY header badge (`recordingModeBadge(...)` output). Only shown when `recording` is not
   * passed at all — a caller that passes `recording` gets the mode in the footer instead.
   */
  mode?: { label: string; tone: StatusTone; title?: string } | null;
  /**
   * This camera's archive, in three distinct states the card must not blur together:
   *   `undefined` — not loaded yet → skeleton.
   *   `null`      — the request failed → says so, because a silent card would read as "empty".
   *   a row       — the truth, including `segment_count: 0`, which the card states out loud.
   */
  storage?: RecordingCameraStorage | null;
  onClick: () => void;
  /** Extra chip in the header (e.g. a site name in a cross-site picker). */
  badge?: ReactNode;
  /** Is the camera reachable? Shown as the header's status line; omitted when undefined. */
  online?: boolean;
  /**
   * The camera's recording config (`IMPLICIT_RECORDING_CONFIG` when it has no row). `null`
   * while the configs are loading (skeleton footer); `undefined` means the caller has no
   * recording footer at all (legacy). See `recordingUnavailable` for a failed load.
   */
  recording?: RecordingConfig | null;
  /** The config request failed — the footer says so rather than guessing a mode. */
  recordingUnavailable?: boolean;
  /**
   * Saves ONE field as a patch — `{ enabled }` or `{ mode }` — so switching the type never
   * resets the camera's tuned pre-roll, sensitivity or retention. The caller owns the transport
   * (local posts to the backend, cloud goes through the tunnel) and the permission check: omit
   * it for users without `recordings.config` and the footer is read-only.
   */
  onRecordingChange?: (patch: RecordingConfig) => Promise<void>;
  /** Modes the node accepts (its config `options.modes`); defaults to every known mode. */
  modes?: string[];
}

/** Bytes → the shortest honest figure, split so the unit can be set smaller than the number. */
export function splitBytes(bytes?: number | null): { value: string; unit: string } {
  const b = Number(bytes) || 0;
  if (b <= 0) return { value: '0', unit: 'B' };
  if (b >= 1e12) return { value: (b / 1e12).toFixed(1), unit: 'TB' };
  if (b >= 1e9) return { value: (b / 1e9).toFixed(1), unit: 'GB' };
  if (b >= 1e6) return { value: (b / 1e6).toFixed(1), unit: 'MB' };
  if (b >= 1e3) return { value: (b / 1e3).toFixed(0), unit: 'KB' };
  return { value: String(b), unit: 'B' };
}

export function formatBytes(bytes?: number | null): string {
  const { value, unit } = splitBytes(bytes);
  return `${value} ${unit}`;
}

/** kbps → the unit a human would say it in. 4096 kbps is "4.1 Mbps" to everyone but a config file. */
export function formatBitrate(kbps?: number | null): string | null {
  const k = Number(kbps) || 0;
  if (k <= 0) return null;
  return k >= 1000 ? `${(k / 1000).toFixed(1)} Mbps` : `${Math.round(k)} kbps`;
}

/**
 * "14 Jun – today" for a card's coverage. Day-level on purpose: the card answers "roughly how far
 * back does this go", and a timestamp would be answering the timeline's question.
 */
export function formatCoverage(from?: string | null, to?: string | null): string | null {
  if (!from) return null;
  const day = (v: string) => new Date(v).toLocaleDateString([], { day: '2-digit', month: 'short' });
  const start = day(from);
  if (!to) return `since ${start}`;
  const isToday = new Date(to).toDateString() === new Date().toDateString();
  const end = isToday ? 'today' : day(to);
  return start === end ? start : `${start} – ${end}`;
}

/**
 * "4 min ago" for the newest clip, within the last day only — past that the coverage line
 * already says the day, and "31 h ago" is worse than a date. This is the card's "is it still
 * writing" answer: a camera on continuous whose last clip is an hour old has a problem.
 */
function formatLastClip(at?: string | null): string | null {
  if (!at) return null;
  const mins = Math.floor((Date.now() - new Date(at).getTime()) / 60000);
  if (!Number.isFinite(mins) || mins < 0 || mins >= 24 * 60) return null;
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  return `${Math.floor(mins / 60)} h ago`;
}

/** `color` at `pct`% over the page. Tailwind's `bg-brand/10` compiles to NOTHING here (the theme
 *  colours are bare `var()`s), so tints are built the way StatusTag builds them. */
const mix = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

/**
 * One stream fact. Quiet, monospaced, and it wraps rather than truncating the row.
 *
 * The hint uses the app's Tooltip rather than a native `title`: the browser's version can't be
 * themed, takes a second to appear, and at this size the chips are exactly the elements whose
 * meaning has to arrive the moment the pointer lands on them.
 */
function Chip({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <Tooltip label={hint} side="top">
      <span className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] leading-tight text-muted-foreground">
        {children}
      </span>
    </Tooltip>
  );
}

/** The on/off switch. Sized for a card footer, not a settings form. */
function Switch({ on, disabled, onToggle }: { on: boolean; disabled?: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Record this camera"
      disabled={disabled}
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ring disabled:cursor-not-allowed disabled:opacity-40 ${
        on ? 'bg-brand' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
    </button>
  );
}

/**
 * The footer: what the camera is set to keep, and — for an operator allowed to — the controls
 * that change it.
 *
 * Turning recording OFF asks first. It stops the pipeline outright ("no footage is written"),
 * and on a grid of sixteen cards a switch is an easy thing to catch by accident; turning it ON
 * and changing the type don't ask, because neither can lose anything already recorded.
 */
function RecordingFooter({
  recording, unavailable, online, onChange, modes,
}: {
  recording?: RecordingConfig | null;
  unavailable?: boolean;
  online?: boolean;
  onChange?: (patch: RecordingConfig) => Promise<void>;
  modes: string[];
}) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  if (unavailable) {
    return <p className="text-[11px] text-muted-foreground">Recording settings unavailable</p>;
  }
  if (!recording) {
    // Still loading — a skeleton rather than a guessed mode.
    return (
      <div className="flex items-center gap-2">
        <div className="h-5 w-9 animate-pulse rounded-full bg-muted" />
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  const enabled = recording.enabled !== false;
  const mode = recording.mode || 'continuous';
  const state = recordingState(recording);
  const editable = !!onChange;

  async function save(patch: RecordingConfig) {
    if (!onChange) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await onChange(patch);
      setSaved(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 1800);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Could not save — try again');
    } finally {
      setSaving(false);
    }
  }

  if (confirmOff) {
    return (
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-[12px] font-medium text-foreground">Stop recording this camera?</p>
        <button
          type="button"
          onClick={() => { setConfirmOff(false); save({ enabled: false }); }}
          className="rounded-md bg-destructive px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90"
        >
          Stop
        </button>
        <button
          type="button"
          onClick={() => setConfirmOff(false)}
          className="rounded-md border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    );
  }

  // An unknown mode the node reports still has to be selectable, or the picker would lie.
  const options = modes.includes(mode) ? modes : [mode, ...modes];
  // The one line under the controls says what the setting MEANS right now — which is not
  // always the mode's hint: an enabled camera that is unreachable is writing nothing.
  const hint = !enabled
    ? 'Not recording — no new footage is written.'
    : online === false
      ? 'Camera offline — nothing is being written right now.'
      : MODE_LABELS[mode]?.hint;

  return (
    <div className="space-y-1.5">
      <div className="flex min-h-[26px] items-center gap-2">
        {editable ? (
          <Tooltip label={enabled ? 'Stop recording this camera' : 'Start recording this camera'} side="top" className="flex-none">
            <Switch on={enabled} disabled={saving} onToggle={() => (enabled ? setConfirmOff(true) : save({ enabled: true }))} />
          </Tooltip>
        ) : null}
        <span className="flex-none text-[12px] font-semibold text-foreground">
          {editable ? (enabled ? 'Recording' : 'Not recording') : 'Recording'}
        </span>
        <span className="flex-1" />
        {saving && (
          <span className="h-3.5 w-3.5 flex-none animate-spin rounded-full border-2 border-brand border-t-transparent" aria-label="Saving" />
        )}
        {saved && !saving && (
          <span className="flex flex-none items-center gap-0.5 text-[11px] font-medium" style={{ color: toneVar('success') }}>
            <CheckIcon className="h-3.5 w-3.5" />Saved
          </span>
        )}
        {editable && enabled ? (
          <select
            aria-label="Recording type"
            value={mode}
            disabled={saving}
            onChange={(e) => save({ mode: e.target.value })}
            className="min-w-0 max-w-[60%] flex-none cursor-pointer rounded-md border border-border bg-card py-1 pl-2 pr-7 text-[12px] font-medium text-foreground transition-colors hover:border-brand focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:cursor-not-allowed disabled:opacity-40"
          >
            {options.map((m) => (
              <option key={m} value={m}>{MODE_LABELS[m]?.label || m}</option>
            ))}
          </select>
        ) : (
          // Read-only, or recording off: the state as a tag. A disabled dropdown would look
          // like something that could be changed.
          (!editable || !enabled) && <StatusTag tone={state.tone}>{state.label}</StatusTag>
        )}
      </div>
      {error ? (
        <p className="text-[11px] leading-snug text-destructive">{error}</p>
      ) : hint ? (
        <p
          className="truncate text-[11px] leading-snug"
          style={{ color: enabled && online === false ? toneVar('warning') : 'var(--muted-foreground)' }}
          title={hint}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function RecordingCameraCard({
  name,
  mode,
  storage,
  onClick,
  badge,
  online,
  recording,
  recordingUnavailable,
  onRecordingChange,
  modes = Object.keys(MODE_LABELS),
}: RecordingCameraCardProps) {
  const clips = storage?.segment_count ?? 0;
  const stored = splitBytes(storage?.total_size);
  const coverage = storage ? formatCoverage(storage.oldest_segment, storage.newest_segment) : null;
  const lastClip = storage ? formatLastClip(storage.newest_segment) : null;
  const bitrate = formatBitrate(storage?.bitrate_kbps);
  const hasFooter = recording !== undefined || !!recordingUnavailable;
  // Off reads as off at a glance: the camera tile goes grey. Its old footage is still there to
  // open, so the rest of the card stays at full strength.
  const recordingOff = recording?.enabled === false;
  const brand = 'var(--brand)';

  return (
    <div
      className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card transition-[border-color,box-shadow] has-[.rc-open:hover]:border-[color:var(--brand)] has-[.rc-open:hover]:shadow-card-hover"
    >
      <button
        type="button"
        onClick={onClick}
        className="rc-open group flex flex-1 flex-col gap-3 p-3.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-ring"
      >
        {/* ── Identity ── */}
        <div className="flex w-full min-w-0 items-center gap-2.5">
          <span
            className="flex h-9 w-9 flex-none items-center justify-center rounded-lg transition-colors"
            style={recordingOff
              ? { backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }
              : { backgroundColor: mix(brand, 12), color: brand }}
          >
            <VideoCameraIcon className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            {/* The full name plus what the card does, themed and instant. Long names truncate
                here, so this is also where an operator reads the ones that don't fit. */}
            <Tooltip label={`${name} — open its recordings`} side="top" className="block min-w-0">
              <span className="block truncate text-[13px] font-semibold text-foreground transition-colors group-hover:text-brand">
                {name}
              </span>
            </Tooltip>
            {online !== undefined && (
              <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span
                  className="h-1.5 w-1.5 flex-none rounded-full"
                  style={{ backgroundColor: toneVar(online ? 'success' : 'danger') }}
                />
                {online ? 'Online' : 'Offline'}
              </span>
            )}
          </div>
          {badge}
          {!hasFooter && mode && (
            <Tooltip label={mode.title} side="top" className="flex-none">
              <StatusTag tone={mode.tone}>{mode.label}</StatusTag>
            </Tooltip>
          )}
          <ChevronRightIcon className="h-4 w-4 flex-none text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-brand" />
        </div>

        {storage === undefined ? (
          /* Not loaded yet — a skeleton rather than a zero, which would be a claim. */
          <div className="space-y-2">
            <div className="h-6 w-20 animate-pulse rounded bg-muted" />
            <div className="h-3 w-32 animate-pulse rounded bg-muted" />
          </div>
        ) : storage === null ? (
          /* The read failed. Deliberately distinct from an empty archive: those are different
             problems, and a card that just says nothing gets read as the second one. */
          <p className="text-[11px] text-muted-foreground">Archive size unavailable</p>
        ) : clips === 0 ? (
          <p className="text-[11px] text-muted-foreground">Nothing recorded yet</p>
        ) : (
          <>
            {/* ── The figure ──
                Stored size, big enough to compare across a grid without reading. The unit is set
                smaller and lighter so the digits stay the thing you see. */}
            <Tooltip
              label={`${(storage.total_size || 0).toLocaleString()} bytes across ${clips.toLocaleString()} segments`}
              side="top"
            >
              <span className="flex items-baseline gap-1">
                <span className="text-[22px] font-bold leading-none tracking-tight tabular-nums text-foreground">
                  {stored.value}
                </span>
                <span className="text-[12px] font-semibold text-muted-foreground">{stored.unit}</span>
                <span className="ml-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">stored</span>
              </span>
            </Tooltip>

            {/* ── The shape ── how many pieces, over what period, how recently. Icons instead of
                labels: at this size a word costs more room than the glyph that means it. */}
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              <Tooltip label={`${clips.toLocaleString()} recorded segments`} side="top" className="flex-none">
                <span className="flex items-center gap-1">
                  <FilmIcon className="h-3.5 w-3.5 flex-none" />
                  <span className="tabular-nums">{clips.toLocaleString()} clips</span>
                </span>
              </Tooltip>
              {coverage && (
                <Tooltip label="The period this camera has footage for" side="top" className="min-w-0">
                  <span className="flex min-w-0 items-center gap-1">
                    <CalendarDaysIcon className="h-3.5 w-3.5 flex-none" />
                    <span className="truncate">{coverage}</span>
                  </span>
                </Tooltip>
              )}
              {lastClip && (
                <Tooltip label="When the newest clip was written" side="top" className="flex-none">
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-3.5 w-3.5 flex-none" />
                    <span>{lastClip}</span>
                  </span>
                </Tooltip>
              )}
            </div>

            {/* ── The stream ──
                What is being written right now. Chips rather than a fourth sentence: these are
                values to glance at and compare down a column, and each is meaningless to widen.
                Only what the segments reported — rows written before the resolution/bitrate columns
                existed carry NULL, so a missing chip is normal and a placeholder would be noise. */}
            {(storage.resolution || storage.codec || bitrate || storage.latest_segment_size) && (
              <div className="flex flex-wrap items-center gap-1">
                {storage.resolution && (
                  // ASCII "x" is what the pipeline stores ("1920x1080"); "×" reads better.
                  <Chip hint="Recorded resolution">{storage.resolution.replace(/x/i, '×')}</Chip>
                )}
                {storage.codec && <Chip hint="Video codec">{storage.codec}</Chip>}
                {bitrate && <Chip hint="Stored bitrate of the most recent segment">{bitrate}</Chip>}
                {storage.latest_segment_size && (
                  <Chip hint="Size of the most recent finished segment">
                    last {formatBytes(storage.latest_segment_size)}
                  </Chip>
                )}
              </div>
            )}
          </>
        )}
      </button>

      {/* ── Recording ── pinned to the bottom (the button above takes the slack), so the footers
          of a row of cards line up however much each body says. */}
      {hasFooter && (
        <div className="border-t border-border bg-background px-3.5 py-2.5">
          <RecordingFooter
            recording={recording}
            unavailable={recordingUnavailable}
            online={online}
            onChange={onRecordingChange}
            modes={modes}
          />
        </div>
      )}
    </div>
  );
}
